import * as THREE from 'three';
import EventDispatcher from '../common/EventDispatcher';
import { gsap, Expo, Cubic } from 'gsap';
import { Global } from '../common/global';
import { isMobile } from 'mobile-device-detect';

const INITIAL_CAMERA_POSITION = new THREE.Vector3(0, 0.5, 9.5 + 0);
const INITIAL_LOOK_AT_POSITION = new THREE.Vector3(0, 0, 0);

const CAMERA_FOLLOW_LERP_X = 0.5;
const CAMERA_FOLLOW_LERP_Y = 0.1;
const CAMERA_FOLLOW_LERP_Z = 0.5;
const CAMERA_LOOKAT_Y_PADD = 1.5;
const DEFAULT_SHAKE_VALUE = {
  value: 0.0,
};

export default class Camera extends THREE.PerspectiveCamera {
  skyLookFactor = new THREE.Vector3(0, 2, -5);
  lerpZReduce = 0;
  lastCameraPosition = INITIAL_CAMERA_POSITION.clone();
  gameStarted = false;

  constructor(fov, ratio, near, far) {
    super(fov, ratio, near, far);
  }

  init() {
    this.events = EventDispatcher.getObj();

    this.events.addEventListener('CAMERA_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('GAMESCENE_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('PLAYER_EVENTS', this.onEvents.bind(this));
    this.events.addEventListener('GAME_EVENTS', this.onEvents.bind(this));

    this.position.copy(INITIAL_CAMERA_POSITION);
    this.initCameraFixed = false;
    this.shakeVal = DEFAULT_SHAKE_VALUE;
    this.lookAt(INITIAL_LOOK_AT_POSITION);

    this.panSpeed = {
      value: 0,
    };
  }

  onEvents(data) {
    switch (data['message']['event_type']) {
      case 'reset_game':
        this.reset();
        break;
      case 'resize':
        this.onResize(data['message']['data']);
        break;
      case 'update':
        this.update();
        break;
      case 'move_ball':
        this.smoothFollowBall(data['message']['data']);
        break;
      case 'shake_camera':
        this.prepareToShake(data['message']['data']);
        break;
      case 'start_game':
        this.gameStarted = true;
        this.initCameraFixed = false;
        break;
      case 'camera_pan_start':
        this.updatePanSpeed();
        break;
      case 'game_over':
        // this.onLevelCompleted();
        break;
      case 'reset_camera':
        this.resetCamera();
        break;
      case 'set_camera_view':
        this.setCameraView(data['message']['data']);
        break;
    }
  }
  updatePanSpeed() {
    gsap.to(this.panSpeed, {
      value: 0.2,
      yoyo: true,
      repeat: 1,
      duration: 0.5,
    });
  }

  setCameraView(data) {
    const distanceBehindPlayer = 3.5;
    const cameraHeight = 3.5;
    const playerPosition = data['position'];
    const playerRotationY = data['rotationY'];
    const lookAtDistance = 5;

    this.cameraPosition = new THREE.Vector3()
      .copy(playerPosition)
      .addScaledVector(data['direction'], -distanceBehindPlayer);
    this.cameraPosition.y += cameraHeight;
    this.position.copy(this.cameraPosition);

    const lookAtPoint = new THREE.Vector3(
      playerPosition.x + Math.sin(playerRotationY) * lookAtDistance,
      playerPosition.y,
      playerPosition.z + Math.cos(playerRotationY) * lookAtDistance
    );

    this.lookAt(lookAtPoint);
  }

  reset() {
    this.initCameraFixed = false;
  }

  onResize(data) {
    this.aspect = data.aspect;
    this.updateProjectionMatrix();
  }

  update() {
    // Apply camera shake effect
    if (this.shakeVal.value > 0) {
      this.position.x += (Math.random() - 0.5) * this.shakeVal.value;
      this.position.y += (Math.random() - 0.5) * this.shakeVal.value;
      // this.position.z += (Math.random() - 0.5) * this.shakeVal.value;
    }
  }

  smoothFollowBall(data) {
    if (!Global.isGameActive) return;
    const ballPosition = data['position'];

    // Calculate the target position for the camera
    const targetPosition = new THREE.Vector3(
      ballPosition.x,
      ballPosition.y, // Keep the camera's Y position fixed or adjust as needed
      ballPosition.z // Keep the camera's Z position fixed or adjust as needed
    );
    // Smoothly interpolate the camera's position towards the target position
    this.position.lerp(targetPosition, data.speed); // Adjust the lerp factor for smoother or faster movement

    // Calculate the target look-at position (ball's position)
    const targetLookAt = new THREE.Vector3(
      ballPosition.x,
      ballPosition.y,
      ballPosition.z
    );

    // Smoothly interpolate the look-at position towards the ball's position
    const currentLookAt = new THREE.Vector3();
    this.getWorldDirection(currentLookAt); // Get the current look-at direction
    currentLookAt.add(this.position); // Convert direction to a position in world space

    const smoothedLookAt = currentLookAt.lerp(
      targetLookAt,
      0.0001 + this.panSpeed.value
    ); // Adjust the lerp factor for slower or faster look-at
    this.lookAt(smoothedLookAt);
  }

  prepareToShake(data) {
    if (Global.gameOver) return false;
    this.shakeVal = {
      value: data['shake_fact'] * 0.25,
    };

    gsap.to(this.shakeVal, {
      duration: data['shake_time'] / 10,
      delay: 0,
      value: 0,
      yoyo: true,
      repeat: 20,
      onUpdate: function () {}.bind(this),
    });
  }

  resetCamera() {
    this.initCameraFixed = false;
    this.lastCameraPosition = INITIAL_CAMERA_POSITION.clone();
    this.skyLookFactor = new THREE.Vector3(0, 2, -5);
    this.lerpZReduce = 0;
    this.position.copy(INITIAL_CAMERA_POSITION);
    this.lookAt(INITIAL_LOOK_AT_POSITION);
    // document.querySelector(".tint").classList.remove('active');
  }
}
